import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import { useNavigate } from "react-router-dom";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

import RestartAltIcon from "@mui/icons-material/RestartAlt";

import DefaultBadge from "../../../components/simple/DefaultBadge";

import SimTablePager from "./Pager";

dayjs.extend(duration);

const SimTable = ({
  simulations,
  theme,
  page,
  onPageChange,
  setDeleteConfirmModal,
  paginationData,
  updateFilters,
  hasActiveFilters,
}) => {
  const navigate = useNavigate();

  const rows = useMemo(() => {
    return Object.values(simulations).map((item) => {
      const createdAt = dayjs(item.created_at);

      const score = item.evaluation?.total_score?.value
        ? (parseFloat(item?.evaluation?.total_score?.value) / 10).toFixed(1) +
          "/10"
        : "N/A";
      // 1. Grab your duration string (assuming it's on item.duration_string, adjust as needed)
      const rawDurationStr = item.duration || "00:00:00.000000";

      // 2. Split the string by the colon
      const [h, m, s] = rawDurationStr.split(":");

      // 3. Feed it into dayjs.duration (rounding the decimal seconds)
      const duration = dayjs.duration({
        hours: parseInt(h, 10),
        minutes: parseInt(m, 10),
        seconds: Math.round(parseFloat(s)), // This rounds 6.462307 down to 6
      });
      const durationDisplay = `${duration.hours()}h ${duration.minutes()}m ${duration.seconds()}s`;

      return {
        id: item.id,
        scenarioName:
          item.type === "conversation_insight"
            ? item.name || "Unknown"
            : item.name || "Unknown",
        description: item.details || "Unknown Simulation Challenge",
        score: score,

        // Exposing the raw ISO string as requested
        created_at: item.created_at,

        // Formatted display values using just created_at
        dateCompleted: createdAt.format("MMM DD, YYYY"),
        timeCompleted: createdAt.format("HH:mm"),
        dateCompletedDisplay: `${createdAt.format("MMM DD, YYYY")}`,
        duration:
          item.type === "conversation_insight" ? "N/A" : durationDisplay,
        type: item.type,
        durationMs: duration.asMilliseconds(),
      };
    });
  }, [simulations]);

  const columns = [
    {
      field: "scenarioName",
      headerName: "Simulation Challenge",
      flex: 1,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            py: 1.5,
            whiteSpace: "normal",
            lineHeight: "1.4",
            fontSize: "14px",
          }}
        >
          <Box component="div" sx={{ fontWeight: "600", color: "white" }}>
            {params.row.scenarioName}
          </Box>
          <Box
            component="div"
            sx={{
              fontSize: "0.75rem",
              opacity: params.row.type === "conversation_insight" ? "1" : "0.5",
              mt: 0.5,
            }}
          >
            {params.row.type === "conversation_insight" ? (
              <div className="convo-insights-badge">
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 11 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.5 7L2.96237 8.55685C2.74789 8.77401 2.64065 8.88259 2.54848 8.89025C2.46851 8.89689 2.39021 8.86474 2.33798 8.80382C2.27778 8.7336 2.27778 8.58099 2.27778 8.27577V7.49581C2.27778 7.22198 2.05354 7.02385 1.7826 6.98416C1.12687 6.88811 0.611888 6.37313 0.515838 5.7174C0.5 5.60928 0.5 5.48026 0.5 5.22222V2.9C0.5 2.05992 0.5 1.63988 0.66349 1.31901C0.8073 1.03677 1.03677 0.8073 1.31901 0.66349C1.63988 0.5 2.05992 0.5 2.9 0.5H6.6C7.44008 0.5 7.86012 0.5 8.18099 0.66349C8.46323 0.8073 8.6927 1.03677 8.83651 1.31901C9 1.63988 9 2.05992 9 2.9V5M9 10.5L7.9118 9.74344C7.75883 9.63709 7.68234 9.58391 7.5991 9.54621C7.52522 9.51275 7.44755 9.48841 7.36779 9.47371C7.27792 9.45714 7.18477 9.45714 6.99846 9.45714H6.1C5.53995 9.45714 5.25992 9.45714 5.04601 9.34815C4.85785 9.25228 4.70487 9.0993 4.60899 8.91113C4.5 8.69722 4.5 8.4172 4.5 7.85714V6.6C4.5 6.03995 4.5 5.75992 4.60899 5.54601C4.70487 5.35785 4.85785 5.20487 5.04601 5.10899C5.25992 5 5.53995 5 6.1 5H8.9C9.46005 5 9.74008 5 9.95399 5.10899C10.1422 5.20487 10.2951 5.35785 10.391 5.54601C10.5 5.75992 10.5 6.03995 10.5 6.6V7.95714C10.5 8.42308 10.5 8.65606 10.4239 8.83983C10.3224 9.08485 10.1277 9.27953 9.88268 9.38102C9.69891 9.45714 9.46594 9.45714 9 9.45714V10.5Z"
                    stroke="#FEFEFE"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Conversation Insight
              </div>
            ) : (
              params.row.description
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: "score",
      headerName: "Score",
      width: 100,
      renderCell: (params) => (
        <Box>
          <Typography
            sx={{
              fontSize: theme.typography.textXS.fontSize,
              display: "flex",
              alignContent: "center",
              justifyContent: "center",
            }}
          >
            {params.row.score !== "N/A" ? (
              <DefaultBadge
                className="hover-badge"
                variant="pillTransparent"
                borderColor={theme.palette.border.brand}
                size="small"
              >
                {params.row.score}
              </DefaultBadge>
            ) : (
              <DefaultBadge size="small">N/A</DefaultBadge>
            )}
          </Typography>
        </Box>
      ),
    },
    // { field: "dateCompleted", headerName: "Date Completed", width: 150 },
    // { field: "timeCompleted", headerName: "Time Completed", width: 150 },
    {
      field: "dateCompletedDisplay",
      headerName: "Date Completed ",
      width: 200,
      renderCell: (params) => params.row.dateCompletedDisplay,
    },
    {
      field: "timeCompleted",
      headerName: "Time Completed",
      width: 200,
      renderCell: (params) => params.row.timeCompleted,
    },
    {
      field: "durationMs",
      headerName: "Duration",
      width: 120,
      renderCell: (params) => params.row.duration,
    },
    {
      field: "actions",
      headerName: "",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          color="error"
          onClick={(event) => {
            event.stopPropagation();
            setDeleteConfirmModal({ show: true, id: params.row.id });
          }}
          sx={{
            margin: "0 auto",
            "& svg path": { color: "#fff", opacity: ".3" },
            "& svg:hover path": { opacity: 1 },
          }}
        >
          <svg
            width="17"
            height="19"
            viewBox="0 0 17 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.6667 4.16683V3.50016C11.6667 2.56674 11.6667 2.10003 11.485 1.74351C11.3252 1.42991 11.0703 1.17494 10.7567 1.01515C10.4001 0.833496 9.93342 0.833496 9 0.833496H7.66667C6.73325 0.833496 6.26654 0.833496 5.91002 1.01515C5.59641 1.17494 5.34145 1.42991 5.18166 1.74351C5 2.10003 5 2.56674 5 3.50016V4.16683M6.66667 8.75016V12.9168M10 8.75016V12.9168M0.833336 4.16683H15.8333M14.1667 4.16683V13.5002C14.1667 14.9003 14.1667 15.6004 13.8942 16.1351C13.6545 16.6055 13.2721 16.988 12.8016 17.2277C12.2669 17.5002 11.5668 17.5002 10.1667 17.5002H6.5C5.09987 17.5002 4.39981 17.5002 3.86503 17.2277C3.39462 16.988 3.01217 16.6055 2.77249 16.1351C2.5 15.6004 2.5 14.9003 2.5 13.5002V4.16683"
              stroke="#cccccc"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {simulations.length > 0 ? (
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={paginationData?.total_items || 0}
          paginationModel={{
            page: page,
            pageSize: paginationData?.page_size || 10,
          }}
          onPaginationModelChange={(newModel) => {
            onPageChange(newModel.page);
          }}
          pageSizeOptions={[5, 10, 25]}
          disableColumnMenu
          onRowClick={(params) => {
            navigate(`/dashboard/feedback/${params.row.id}`);
          }}
          slots={{
            pagination: SimTablePager,
            columnSortedAscendingIcon: ExpandLessIcon,
            columnSortedDescendingIcon: ExpandMoreIcon,
            columnUnsortedIcon: UnfoldMoreIcon,
            paginationPreviousIcon: ChevronLeftIcon,
            paginationNextIcon: ChevronRightIcon,
          }}
          sx={{
            backgroundColor: "transparent",
            border: "none",
            color: "inherit",
            "& .MuiDataGrid-main": {
              backgroundColor: "transparent !important",
            },
            "& .MuiDataGrid-filler": {
              backgroundColor: "transparent !important",
              display: "none",
            },

            "& .MuiDataGrid-topContainer": {
              "--rowBorderColor": "transparent",
            },

            "& .MuiDataGrid-container--top-container": {
              backgroundColor: "transparent !important",
            },

            "& .MuiDataGrid-iconButtonContainer": {
              visibility: "visible", // Ensures the icon container is always visible
            },
            "& .MuiDataGrid-sortIcon, & .MuiDataGrid-sortButton": {
              opacity: "0.7 !important", // Makes sorting icons white and always visible
              color: "#FFFFFF",
            },
            // 3. Remove Backgrounds from all IconButtons (Header & Pagination)
            "& .MuiIconButton-root": {
              backgroundColor: "transparent !important",
              color: "#FFFFFF", // Global icon color to white
              "&:hover": {
                backgroundColor: "transparent !important", // Removes the circular hover background
                opacity: 0.8,
              },
            },

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent !important",
              borderBottom: "1px solid rgb(33, 31, 69)",
              color: "rgb(168, 126, 255)",
              fontSize: "12px",

              "& .MuiDataGrid-columnHeaderRow": {
                "& > div": {
                  border: "0!important",
                },
                "--rowBorderColor": "transparent",
              },
            },

            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "transparent !important",
              padding: "10px 15px",
              border: "0!important",
            },

            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },

            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid rgb(33, 31, 69)",
              display: "flex",
              alignItems: "center",
              padding: "10px 15px",
              whiteSpace: "normal !important",
              wordBreak: "break-word",
              fontSize: "12px",
            },
            "& .MuiDataGrid-row": {
              "--rowBorderColor": "transparent",
              "&:hover .MuiDataGrid-cell": {
                borderColor: "rgb(1, 236, 255)",
              },
              "&:hover div[data-field='scenarioName'] div > div:first-of-type":
                {
                  color: "rgb(1, 236, 255)",
                },
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgb(2, 28, 100) !important",
              transition: "background-color 0.2s ease",
              cursor: "pointer",
            },

            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              color: "rgb(168, 126, 255)",
              "& .MuiTablePagination-root, & .MuiIconButton-root": {
                color: "inherit",
              },
              paddingBottom: "20px",
            },

            // Focus cleanup
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
              outline: "none",
            },
          }}
        />
      ) : (
        <div className="no-results">
          {hasActiveFilters ? (
            <>
              <strong>No results matching your search criteria</strong>
              <button
                onClick={() =>
                  updateFilters({
                    date_range_start: "",
                    date_range_end: "",
                    sim_types: "",
                  })
                }
              >
                Reset Filters <RestartAltIcon />
              </button>
            </>
          ) : (
            <>No simulations to show</>
          )}
        </div>
      )}
    </Box>
  );
};

export default SimTable;
